import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowUpRight, Building2, LineChart, Lock, ShieldCheck, Check, Hexagon, Triangle, Box, CircleDashed, Square, TrendingUp, ChevronDown, Menu, X } from "lucide-react";
import NearMeSearch from "../components/NearMeSearch";
import DemoRequestForm from "../components/DemoRequestForm";
import api from "../lib/api";
import SEO from "../components/SEO";

const PLANS = [
  {
    id: "loans_only",
    name: "Loans Only",
    price: 69,
    desc: "For lenders, brokers, and operators who only need the loan-stack tools.",
    features: [
      "Unlimited loans tracked & accounted for",
      "Full amortization schedules + GAAP-style payment accounting",
      "Mortgage Calculator with Good / Poor Purchase verdict",
      "Refinance Modeler + rate-shock stress test (+100/+200/+300 bps)",
      "Borrowers Library with FICO, net worth, linked loans",
      "Loan-maturity alerts + maturity-date overrides",
      "Reports → Loan Portfolio (PDF + email to lender)",
      "No property / tenant / lease management",
      "Add the Personal Financial Statement generator for +$20/mo",
    ],
    featured: false,
  },
  {
    id: "starter",
    name: "Starter",
    price: 29,
    desc: "For owners managing a handful of commercial assets.",
    features: [
      "Up to 5 properties",
      "Unlimited tenants & leases",
      "Online rent collection (Stripe)",
      "Portfolio Snapshot + Rent Roll PDFs",
      "Tenant portal + maintenance tickets",
      "Solo account (no teammates)",
      "Add the Personal Financial Statement generator for +$20/mo",
    ],
    featured: false,
  },
  {
    id: "pro",
    name: "Pro",
    price: 99,
    desc: "For growing portfolios with multiple units and leases.",
    features: [
      "Unlimited properties",
      "3 teammate seats with internal messaging + @-mentions",
      "Unlimited AI Lease Generation with state-compliance scans",
      "Vendor Portal — one-click 'Send login' email invites",
      "Borrower Library with linked loan records",
      "Loan-maturity alerts (sidebar badge + dashboard)",
      "Loan amortization, accounting & rate-shock stress test",
      "Schedule of Real Estate Owned + Tax & Insurance coverage check",
      "Property Taxes Due report — date-range PDF + 30-day dashboard alert",
      "One-click Lender Packet email (SREO + live rent roll)",
      "Bring-your-own Stripe + PayPal gateway",
      "Bring-your-own SMTP / IMAP email accounts",
      "Cap-rate benchmarking + portfolio performance analytics",
      "Vendor invoice inbox + 1099-NEC export",
      "Counter-offer history & lease redline tracking",
      "Priority email support",
    ],
    featured: true,
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: 199,
    desc: "For property firms with large commercial portfolios.",
    features: [
      "Everything in Pro",
      "Unlimited properties & tenants",
      "15 teammate seats",
      "Plaid-verified ACH bank payments",
      "PFS Personal Financial Statement + Lender Package builder",
      "Custom investor reporting + workspace share links + API access",
      "Multi-tenant workspace architecture",
      "Dedicated account manager + SLA",
      "Priority phone + email support",
    ],
    featured: false,
  },
];

const sponsors = [
  { icon: Hexagon, name: "NEXUS CRE" },
  { icon: Triangle, name: "APEX HOLD" },
  { icon: Box, name: "METROPARK" },
  { icon: CircleDashed, name: "IVY EQUITY" },
  { icon: Square, name: "VERTEX RE" },
];

export default function Landing() {
  const [revenue, setRevenue] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/public/stats/revenue-this-month")
      .then((r) => {
        if (!cancelled) setRevenue(r.data);
      })
      .catch(() => {
        /* fail silent — banner simply won't render */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen bg-marketing text-white">
      <SEO
        title="Commercial Real-Estate Portfolio Management Software"
        description="RealtyVault Manager — AI lease generation, online rent collection, loan amortization, one-click Lender Package, multi-LLC workspaces, and investor reporting. Built for serious CRE operators."
        path="/"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "RealtyVault Manager",
            applicationCategory: "BusinessApplication",
            applicationSubCategory: "Commercial Real Estate Portfolio Management",
            operatingSystem: "Web (any modern browser)",
            description: "Commercial real-estate portfolio management platform. Collect rent online, track NOI and cap rate live, run Schedule of Real Estate Owned reports, generate investor digests, and manage loans + expenses in one place.",
            url: "https://realtyvaultmgr.com/",
            image: "https://realtyvaultmgr.com/marketing/og-share.jpg",
            screenshot: "https://realtyvaultmgr.com/marketing/og-share.jpg",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "USD",
              description: "Free workspace — start with no credit card. Paid plans scale with your portfolio.",
            },
            featureList: [
              "Multi-tenant CRE portfolio management",
              "Online rent collection via Stripe Connect",
              "Live NOI, cap rate, DSCR, LTV tracking",
              "Schedule of Real Estate Owned (SREO) with per-property rent roll",
              "One-click branded PDF reports to lenders",
              "Loan amortization and payment tracking",
              "Expense management with vendor categories",
              "Automated investor digests",
              "Tenant payment portal",
              "Interactive cap-rate benchmarks",
            ],
            publisher: { "@type": "Organization", name: "RealtyVault Manager Inc." },
          },
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "RealtyVault Manager",
            url: "https://realtyvaultmgr.com",
            logo: "https://realtyvaultmgr.com/marketing/og-share.jpg",
            sameAs: ["https://twitter.com/realtyvaultmgr"],
            contactPoint: {
              "@type": "ContactPoint",
              contactType: "customer support",
              email: "support@realtyvaultmgr.com",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "RealtyVault Manager",
            url: "https://realtyvaultmgr.com",
            potentialAction: {
              "@type": "SearchAction",
              target: "https://realtyvaultmgr.com/find-space?address={search_term_string}",
              "query-input": "required name=search_term_string",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "VideoObject",
            name: "RealtyVault Manager — 30-second narrated investor pitch",
            description: "Every quarter your lender asks for the same package. We built it in one click. Your branded Schedule of Real Estate Owned, rent roll, logo, and lender list — ready to email.",
            thumbnailUrl: "https://realtyvaultmgr.com/marketing/narrated.jpg",
            uploadDate: "2026-04-24",
            duration: "PT30S",
            contentUrl: "https://realtyvaultmgr.com/marketing/narrated.mp4",
            embedUrl: "https://realtyvaultmgr.com/marketing-kit",
          },
          {
            "@context": "https://schema.org",
            "@type": "VideoObject",
            name: "RealtyVault Manager — feature showcase",
            description: "Your commercial portfolio on one screen. Live cap rate tracking. SREO in one click. Email the branded PDF to your lender.",
            thumbnailUrl: "https://realtyvaultmgr.com/marketing/feature.jpg",
            uploadDate: "2026-04-24",
            duration: "PT12S",
            contentUrl: "https://realtyvaultmgr.com/marketing/feature.mp4",
            embedUrl: "https://realtyvaultmgr.com/marketing-kit",
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "Who is RealtyVault Manager built for?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Commercial real-estate investors running 2-200 properties who want to stop wrestling spreadsheets. We're purpose-built for CRE (office, retail, multifamily, industrial, mixed-use) — not residential landlords.",
                },
              },
              {
                "@type": "Question",
                name: "Can my tenants pay rent online?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes. Tenants get their own portal, pay rent via card or ACH through Stripe Connect, and see their full payment history. You receive the funds directly into your connected Stripe account — we never hold your money.",
                },
              },
              {
                "@type": "Question",
                name: "Does it generate a Schedule of Real Estate Owned (SREO) for my lender?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes. One click produces your branded SREO with per-property cost basis, current market value, NOI, cap rate, loan balance, equity, DSCR, and LTV — plus optional nested rent-roll per property. Download the PDF or email it directly to a saved lender contact list.",
                },
              },
              {
                "@type": "Question",
                name: "How does cap-rate and NOI tracking work?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Live. Every rent payment, vendor invoice, loan payment, and stored annual expense (taxes + insurance) updates your NOI, cap rate, DSCR, and LTV in real time. No monthly close; no end-of-quarter spreadsheet refresh.",
                },
              },
              {
                "@type": "Question",
                name: "Is there a free trial?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes — start a free workspace with no credit card required. You can add properties, invite tenants, and generate reports immediately. We don't charge until you're ready to scale.",
                },
              },
              {
                "@type": "Question",
                name: "What makes this different from Buildium or AppFolio?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We're CRE-first, investor-first, and report-first. Cap-rate benchmarking, IRR and DSCR math, branded covenant-grade SREO + rent roll, automated investor digests, and on-demand valuation are first-class features — not add-ons. The tenant portal is a side effect, not the centerpiece.",
                },
              },
            ],
          },
        ]}
      />
      {/* NAV */}
      <header className="relative z-20 px-4 sm:px-6 md:px-12 lg:px-24 py-5 md:py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="home-logo">
          <div className="w-8 h-8 bg-white text-slate-900 flex items-center justify-center font-bold">R</div>
          <span className="font-bold tracking-tight text-base sm:text-lg" style={{fontFamily:"Outfit"}}>RealtyVault <span className="font-light text-white/50">Manager</span></span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm text-white/70">
          <a href="#features" className="hover:text-white transition-colors" data-testid="nav-features">Platform</a>
          <a href="#demo" className="hover:text-white transition-colors" data-testid="nav-demo">Book a demo</a>
          <a href="#pricing" className="hover:text-white transition-colors" data-testid="nav-pricing">Pricing</a>
          <a href="#watch" className="hover:text-white transition-colors" data-testid="nav-watch">Watch</a>
          <Link to="/about" className="hover:text-white transition-colors" data-testid="nav-about">About</Link>
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <Link to="/login" data-testid="landing-login-btn" className="text-sm font-medium text-white/80 hover:text-white transition-colors">Sign in</Link>
          <Link to="/signup" data-testid="landing-signup-btn" className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-5 py-2 text-sm font-bold transition-colors duration-200 flex items-center gap-1 whitespace-nowrap">
            Start free <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        {/* Mobile hamburger */}
        <button
          type="button"
          onClick={() => setMobileNavOpen(true)}
          aria-label="Open menu"
          data-testid="landing-mobile-nav-open"
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-full bg-white/10 border border-white/20 text-white hover:bg-white/15 transition-colors"
        >
          <Menu className="w-5 h-5" />
        </button>
      </header>

      {/* Mobile slide-out nav */}
      {mobileNavOpen && (
        <div className="md:hidden fixed inset-0 z-[9999]" data-testid="landing-mobile-nav-panel">
          <div
            className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm"
            onClick={() => setMobileNavOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute top-0 right-0 h-full w-80 max-w-[85vw] bg-slate-950 border-l border-white/10 p-6 flex flex-col gap-6 shadow-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-white text-slate-900 flex items-center justify-center font-bold">R</div>
                <span className="font-bold tracking-tight text-white text-base" style={{fontFamily:"Outfit"}}>RealtyVault</span>
              </div>
              <button
                type="button"
                onClick={() => setMobileNavOpen(false)}
                aria-label="Close menu"
                data-testid="landing-mobile-nav-close"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex flex-col gap-1 text-base text-white/80">
              <a href="#features" onClick={() => setMobileNavOpen(false)} className="py-3 border-b border-white/10 hover:text-white" data-testid="mobile-nav-features">Platform</a>
              <a href="#demo" onClick={() => setMobileNavOpen(false)} className="py-3 border-b border-white/10 hover:text-white" data-testid="mobile-nav-demo">Book a demo</a>
              <a href="#pricing" onClick={() => setMobileNavOpen(false)} className="py-3 border-b border-white/10 hover:text-white" data-testid="mobile-nav-pricing">Pricing</a>
              <a href="#watch" onClick={() => setMobileNavOpen(false)} className="py-3 border-b border-white/10 hover:text-white" data-testid="mobile-nav-watch">Watch</a>
              <Link to="/about" onClick={() => setMobileNavOpen(false)} className="py-3 border-b border-white/10 hover:text-white" data-testid="mobile-nav-about">About</Link>
              <Link to="/tenant-login" onClick={() => setMobileNavOpen(false)} className="py-3 border-b border-white/10 hover:text-white" data-testid="mobile-nav-tenant">Tenant sign in</Link>
            </nav>
            <div className="mt-auto flex flex-col gap-3">
              <Link to="/login" onClick={() => setMobileNavOpen(false)} data-testid="mobile-nav-login" className="w-full text-center border border-white/20 hover:bg-white/10 rounded-full px-5 py-3 text-sm font-semibold text-white transition-colors">
                Sign in
              </Link>
              <Link to="/signup" onClick={() => setMobileNavOpen(false)} data-testid="mobile-nav-signup" className="w-full text-center bg-white text-slate-900 hover:bg-slate-100 rounded-full px-5 py-3 text-sm font-bold transition-colors inline-flex items-center justify-center gap-1">
                Start free workspace <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-texture opacity-60" />
        <div className="absolute inset-0" style={{background: "radial-gradient(700px 300px at 85% 20%, rgba(37,99,235,0.28), transparent 60%)"}} />
        <div className="relative z-10 px-4 sm:px-6 md:px-12 lg:px-24 pt-8 sm:pt-12 pb-20 sm:pb-28 max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7 stagger-in">
              <div className="flex items-center gap-2 mb-8">
                <span className="ticker-dot" />
                <span className="overline text-white/70">Commercial Real Estate OS · 2026</span>
              </div>
              {revenue && revenue.amount > 0 && (
                <div
                  className="inline-flex items-center gap-3 mb-7 pl-3 pr-5 py-2 rounded-full bg-gradient-to-r from-emerald-500/15 via-emerald-400/10 to-transparent border border-emerald-400/30 backdrop-blur-sm"
                  data-testid="landing-revenue-anchor"
                  title={`Updated ${new Date(revenue.as_of).toLocaleString()}`}
                >
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                  </span>
                  <TrendingUp className="w-3.5 h-3.5 text-emerald-300" />
                  <span className="text-xs text-white/80 font-medium">
                    <span className="font-mono text-emerald-300 font-bold" data-testid="landing-revenue-amount">
                      {revenue.amount_display}
                    </span>{" "}
                    in rent collected across{" "}
                    <span className="font-mono text-white font-bold" data-testid="landing-revenue-property-count">
                      {revenue.property_count}
                    </span>{" "}
                    {revenue.property_count === 1 ? "property" : "properties"} this {revenue.month_label.split(" ")[0]}
                  </span>
                </div>
              )}
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95]" style={{fontFamily:"Outfit"}}>
                Run your commercial<br/>
                portfolio like a<br/>
                <span className="italic font-light text-white/70">publicly traded REIT.</span>
              </h1>
              <p className="mt-6 sm:mt-8 text-base sm:text-lg text-white/60 max-w-xl leading-relaxed">
                RealtyVault Manager is the owner console and tenant portal for modern CRE. Track assets, collect rent online,
                and ship investor-grade reports in minutes — not quarters.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-3">
                <Link to="/signup" data-testid="hero-cta-signup" className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-7 py-3.5 font-bold flex items-center gap-2 transition-colors duration-200">
                  Launch free workspace <ArrowUpRight className="w-4 h-4" />
                </Link>
                <a href="#pricing" data-testid="hero-cta-pricing" className="text-white/70 hover:text-white px-5 py-3.5 text-sm font-semibold transition-colors">
                  See pricing →
                </a>
              </div>
              <div className="mt-12 flex items-center gap-6 text-white/40 text-xs">
                <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> SOC 2 aligned</div>
                <div className="flex items-center gap-2"><Lock className="w-4 h-4" /> Stripe secured</div>
                <div className="flex items-center gap-2"><LineChart className="w-4 h-4" /> Real-time ledgers</div>
              </div>
            </div>

            {/* Right side stats card */}
            <div className="lg:col-span-5">
              <div className="glass rounded-2xl p-6 shadow-[0_30px_90px_-20px_rgba(0,0,0,0.5)]">
                <div className="flex items-center justify-between">
                  <div className="overline text-white/50">Q1 · Portfolio Snapshot</div>
                  <div className="text-[10px] text-green-400 font-mono">▲ +12.4%</div>
                </div>
                <div className="mt-4 font-mono text-5xl font-bold tracking-tight">$2.41M</div>
                <div className="text-sm text-white/50 mt-1">Rent collected (YTD)</div>

                <div className="mt-6 grid grid-cols-2 gap-4">
                  <div className="border border-white/10 p-4 rounded-lg">
                    <div className="overline text-white/40">Properties</div>
                    <div className="mt-2 text-2xl font-bold font-mono">37</div>
                  </div>
                  <div className="border border-white/10 p-4 rounded-lg">
                    <div className="overline text-white/40">Occupancy</div>
                    <div className="mt-2 text-2xl font-bold font-mono">94%</div>
                  </div>
                </div>

                <div className="mt-5 border-t border-white/10 pt-4 space-y-2">
                  {[
                    ["Midtown Office Tower", "+$48,200", "paid"],
                    ["Cedar Industrial Park", "+$22,500", "paid"],
                    ["Riverside Retail Plaza", "$14,800", "pending"],
                  ].map(([name, amt, status]) => (
                    <div key={name} className="flex items-center justify-between text-xs">
                      <span className="text-white/70">{name}</span>
                      <span className={`font-mono ${status === "paid" ? "text-green-400" : "text-amber-300"}`}>{amt}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Sponsors */}
          <div className="mt-24 border-t border-white/10 pt-8">
            <div className="overline text-white/30 mb-6 text-center">Trusted by CRE operators nationwide</div>
            <div className="flex flex-wrap items-center justify-center gap-10">
              {sponsors.map((s) => (
                <div key={s.name} className="flex items-center gap-2 text-white/40 hover:text-white/80 transition-colors">
                  <s.icon className="w-5 h-5" />
                  <span className="font-bold tracking-widest text-xs">{s.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MARKETING VIDEO — autoplay muted loop, premium cinematic feel */}
      <section id="video" className="relative bg-slate-950 py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(ellipse_at_top,rgba(251,191,36,0.15),transparent_60%)]" aria-hidden="true" />
        <div className="relative max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <div className="overline text-amber-300/80">Watch</div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mt-2" style={{ fontFamily: "Outfit" }}>
              Commercial real estate, managed like software.
            </h2>
            <p className="text-white/60 mt-3 max-w-2xl mx-auto">
              See how RealtyVault turns a fragmented spreadsheet-and-email workflow into a calm,
              unified operating system for your entire portfolio.
            </p>
          </div>
          <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.8)] bg-slate-900 aspect-video" data-testid="landing-hero-video">
            <video
              src="/marketing/hero.mp4"
              autoPlay muted loop playsInline
              poster="/marketing/hero.jpg"
              className="w-full h-full object-cover"
            >
              Your browser does not support the video tag.
            </video>
            {/* Subtle caption overlay since video is silent */}
            <div className="pointer-events-none absolute inset-0 flex items-end bg-gradient-to-t from-black/50 via-transparent to-transparent">
              <div className="p-6 md:p-8 text-white">
                <div className="overline text-amber-300/80">RealtyVault Manager</div>
                <div className="text-lg md:text-2xl font-bold mt-1" style={{ fontFamily: "Outfit" }}>
                  Collect rent · Track NOI · Own your investor relationships.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES - Bento */}
      <section id="features" className="px-4 sm:px-6 md:px-12 lg:px-24 py-16 md:py-24 max-w-7xl mx-auto">
        <div className="max-w-2xl mb-10 md:mb-14">
          <div className="overline text-white/40 mb-4">The Platform</div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter" style={{fontFamily:"Outfit"}}>
            Everything your property team runs on, <span className="text-white/40 italic font-light">unified.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          {/* Big tile */}
          <div className="md:col-span-4 md:row-span-2 glass rounded-2xl p-8 relative overflow-hidden min-h-[380px]">
            <div className="overline text-white/40">01 · Owner Console</div>
            <h3 className="mt-4 text-3xl font-bold tracking-tight" style={{fontFamily:"Outfit"}}>Every property, every payment, one ledger.</h3>
            <p className="mt-3 text-white/60 text-sm max-w-md">
              Track acquisitions, market values, unit occupancy, and rent rolls across your commercial portfolio.
              Built for owners, asset managers, and CFOs.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-3">
              {[
                ["NOI", "$847K"],
                ["Cap Rate", "6.8%"],
                ["CoC", "9.1%"],
              ].map(([k, v]) => (
                <div key={k} className="border border-white/10 p-3 rounded-lg">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">{k}</div>
                  <div className="mt-1 font-mono text-xl">{v}</div>
                </div>
              ))}
            </div>
            <div className="absolute -right-20 -bottom-20 w-80 h-80 rounded-full" style={{background:"radial-gradient(circle, rgba(37,99,235,0.25) 0%, transparent 60%)"}} />
          </div>

          <div className="md:col-span-2 glass rounded-2xl p-6">
            <Building2 className="w-6 h-6 text-white/70" />
            <h3 className="mt-4 text-xl font-bold tracking-tight" style={{fontFamily:"Outfit"}}>Property Register</h3>
            <p className="mt-2 text-white/50 text-sm">Office, retail, industrial, multi-family, hospitality — unified schema with cap-rate benchmarks per asset class.</p>
          </div>

          <div className="md:col-span-2 glass rounded-2xl p-6">
            <LineChart className="w-6 h-6 text-white/70" />
            <h3 className="mt-4 text-xl font-bold tracking-tight" style={{fontFamily:"Outfit"}}>Live Reports</h3>
            <p className="mt-2 text-white/50 text-sm">Rent roll, payments PDF + CSV, SREO, cap-rate performance, T&amp;I coverage, P&amp;L — all landscape-formatted, lender-ready.</p>
          </div>

          <div className="md:col-span-3 glass rounded-2xl p-6">
            <div className="overline text-white/40">02 · Lender-Ready</div>
            <h3 className="mt-3 text-2xl font-bold tracking-tight" style={{fontFamily:"Outfit"}}>One-click Lender Packet email.</h3>
            <p className="mt-2 text-white/50 text-sm">SREO + current-month rent roll, both in matching landscape branding. Stress-test every loan at +100/+200/+300 bps before refi conversations.</p>
          </div>

          <div className="md:col-span-3 glass rounded-2xl p-6">
            <div className="overline text-white/40">03 · Tenant + Vendor Portal</div>
            <h3 className="mt-3 text-2xl font-bold tracking-tight" style={{fontFamily:"Outfit"}}>Rent in 90 seconds. Vendor invoices in one click.</h3>
            <p className="mt-2 text-white/50 text-sm">Stripe-powered rent collection. Vendors submit invoices via magic link, you approve in bulk, 1099-NEC exports at year-end.</p>
          </div>
        </div>
      </section>

      {/* BOOK A DEMO — converts video-watchers into qualified leads */}
      <section id="demo" className="relative bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(ellipse_at_center,rgba(251,191,36,0.2),transparent_60%)]" aria-hidden="true" />
        <div className="relative max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <div className="overline text-amber-300/80">30 minutes · No pressure</div>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white mt-3" style={{ fontFamily: "Outfit" }}>
              Bring your portfolio.<br className="hidden md:block" />{" "}
              <span className="text-amber-300/90">We'll show you the OS behind it.</span>
            </h2>
            <p className="text-white/60 mt-4 max-w-2xl mx-auto">
              Pick a time. We'll walk you through rent collection, NOI tracking, loan amortization + rate-shock stress testing,
              SREO + Lender Packet email, and tax & insurance coverage analysis — tailored to properties like yours.
            </p>
          </div>
          <DemoRequestForm />
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="px-4 sm:px-6 md:px-12 lg:px-24 py-16 md:py-24 max-w-7xl mx-auto">
        <div className="max-w-2xl mb-10 md:mb-14">
          <div className="overline text-white/40 mb-4">Pricing</div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter" style={{fontFamily:"Outfit"}}>
            One subscription. <span className="text-white/40 italic font-light">No transaction fees from us.</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              data-testid={`pricing-${plan.id}`}
              className={`rounded-2xl p-6 sm:p-8 flex flex-col ${
                plan.featured
                  ? "bg-white text-slate-900"
                  : "glass text-white"
              }`}
            >
              <div className={`overline ${plan.featured ? "text-slate-500" : "text-white/40"}`}>{plan.name}</div>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-5xl font-bold font-mono tracking-tight">${plan.price}</span>
                <span className={plan.featured ? "text-slate-500" : "text-white/50"}>/month</span>
              </div>
              <p className={`mt-3 text-sm ${plan.featured ? "text-slate-600" : "text-white/50"}`}>{plan.desc}</p>
              <ul className="mt-6 space-y-3 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm">
                    <Check className={`w-4 h-4 ${plan.featured ? "text-slate-900" : "text-green-400"}`} /> {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/signup"
                data-testid={`pricing-cta-${plan.id}`}
                className={`mt-8 rounded-full py-3 text-center font-bold transition-colors duration-200 ${
                  plan.featured
                    ? "bg-slate-900 text-white hover:bg-slate-800"
                    : "bg-white text-slate-900 hover:bg-slate-100"
                }`}
              >
                Start with {plan.name}
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* PROPERTIES NEAR ME — prospect lead magnet */}
      <section id="near-me" className="px-4 sm:px-6 md:px-12 lg:px-24 pt-4 pb-16 md:pb-24 max-w-7xl mx-auto">
        <div className="max-w-2xl mb-10">
          <div className="overline text-white/40 mb-4">For prospects</div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter" style={{ fontFamily: "Outfit" }}>
            Scouting a neighborhood? <span className="text-white/40 italic font-light">Start here.</span>
          </h2>
          <p className="mt-4 text-white/50">
            Punch in a ZIP or address, pick a radius, and see every commercial property our owners are ready to discuss — with direct contact in one click.
          </p>
        </div>
        <NearMeSearch theme="dark" />

        {/* Tenant broadcast CTA — can't find what you need? */}
        <div className="mt-10 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-md p-6 md:p-8 flex flex-wrap items-center justify-between gap-5" data-testid="landing-find-space-cta">
          <div className="max-w-xl">
            <div className="overline text-white/40 mb-2">For tenants</div>
            <h3 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>
              Don't see a fit? <span className="text-white/40 italic font-light">Broadcast what you need.</span>
            </h3>
            <p className="mt-2 text-white/60 text-sm">
              One form reaches every investor whose portfolio matches your criteria — no broker calls required.
            </p>
          </div>
          <Link
            to="/find-space"
            data-testid="landing-find-space-btn"
            className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-6 py-3 font-bold flex items-center gap-2 transition-colors"
          >
            Broadcast a request <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* WATCH — neutral value-prop block replacing the testimonial.
          Pairs the 30-second narrated pitch with factual feature callouts.
          NO fake reviews / aggregateRating — schema-safe + policy-compliant. */}
      <section id="watch" className="px-4 sm:px-6 md:px-12 lg:px-24 py-16 md:py-24 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-10 md:gap-12 items-center">
          <div>
            <div className="overline text-white/40 mb-4">See it in 30 seconds</div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter leading-[1.05]" style={{ fontFamily: "Outfit" }}>
              Every quarter, your lender <span className="text-white/40 italic font-light">asks the same thing.</span>
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mt-6 max-w-xl">
              Market values. Loan balances. Cap rates. Rent roll. DSCR.
              Stop rebuilding that package from spreadsheets. RealtyVault Manager generates the
              branded Schedule of Real Estate Owned — with your logo, your lender list, your data —
              in a single click.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/signup"
                data-testid="watch-cta-signup"
                className="bg-amber-400 hover:bg-amber-300 text-slate-900 rounded-full px-6 py-3 font-bold flex items-center gap-2 transition-colors"
              >
                Start free workspace <ArrowUpRight className="w-4 h-4" />
              </Link>
              <a
                href="#demo"
                data-testid="watch-cta-demo"
                className="border border-white/20 hover:bg-white/5 rounded-full px-6 py-3 font-semibold text-sm transition-colors"
              >
                Book a demo
              </a>
            </div>
          </div>

          <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.8)] bg-slate-900 aspect-video" data-testid="watch-video">
            <video
              src="/marketing/narrated.mp4"
              poster="/marketing/narrated.jpg"
              controls
              playsInline
              preload="metadata"
              className="w-full h-full object-cover"
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </div>

        {/* Factual feature tiles — all verifiable in the product itself, no ratings. */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-14">
          {[
            { k: "Live", v: "NOI · cap rate · DSCR · LTV" },
            { k: "1-click", v: "Schedule of Real Estate Owned" },
            { k: "Stripe", v: "Direct rent payouts to you" },
            { k: "Branded", v: "PDFs with your logo" },
          ].map(({ k, v }) => (
            <div key={k} className="glass rounded-2xl p-6">
              <div className="text-3xl font-mono font-bold">{k}</div>
              <div className="mt-2 text-xs uppercase tracking-widest text-white/40 font-bold leading-relaxed">{v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ — mirrors the JSON-LD FAQPage schema in index.html */}
      <section id="faq" className="px-4 sm:px-6 md:px-12 lg:px-24 py-16 md:py-24 max-w-5xl mx-auto">
        <div className="max-w-2xl mb-10 md:mb-14">
          <div className="overline text-white/40 mb-4">FAQ</div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter" style={{ fontFamily: "Outfit" }}>
            Questions investors <span className="text-white/40 italic font-light">actually ask.</span>
          </h2>
        </div>
        <div className="divide-y divide-white/10 border-y border-white/10">
          {FAQS.map((q, i) => (
            <FaqItem key={q.q} q={q.q} a={q.a} defaultOpen={i === 0} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 sm:px-6 md:px-12 lg:px-24 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto glass rounded-3xl p-8 sm:p-10 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <div className="overline text-white/40 mb-3">Ready when you are</div>
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight max-w-xl" style={{fontFamily:"Outfit"}}>Move your portfolio on-chain of record this week.</h3>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/signup" data-testid="footer-cta-signup" className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-6 sm:px-7 py-3.5 font-bold flex items-center gap-2 transition-colors">
              Start free workspace <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to="/tenant-login" data-testid="footer-tenant-login" className="border border-white/20 hover:bg-white/5 rounded-full px-6 sm:px-7 py-3.5 font-semibold text-sm transition-colors">
              Tenant sign in
            </Link>
          </div>
        </div>
      </section>

      <section
        className="px-4 sm:px-6 md:px-12 lg:px-24 py-10 md:py-12 border-t border-white/10 bg-gradient-to-b from-transparent to-white/[0.02]"
        data-testid="developer-credit"
      >
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/15 bg-white/5 text-[10px] font-mono uppercase tracking-[0.2em] text-white/60 mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            From the desk of the founder
          </div>
          <p className="text-base md:text-lg text-white/75 leading-relaxed" style={{ fontFamily: "Outfit" }}>
            Developed by{" "}
            <span className="text-white font-semibold">David Porter</span>
            <span className="block md:inline md:ml-1 text-white/55"> — Encore Business Services Group, LLC</span>
            <span className="block mt-2 text-sm md:text-base text-white/55">
              Bringing to investors a real estate management and commercial loan management tools combined all together in one unified platform.
            </span>
          </p>
        </div>
      </section>

      <footer className="px-4 sm:px-6 md:px-12 lg:px-24 py-8 md:py-10 border-t border-white/10 text-sm text-white/40 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>© 2026 RealtyVault Manager Inc. · Commercial real estate operations platform</div>
        <div className="flex flex-wrap gap-4 sm:gap-6">
          <Link to="/about" className="hover:text-white transition-colors" data-testid="footer-about">About</Link>
          <a href="/#faq" className="hover:text-white transition-colors" data-testid="footer-faq">FAQ</a>
          <Link to="/marketing-kit" className="hover:text-white transition-colors" data-testid="footer-marketing-kit">Marketing Kit</Link>
          <a href="#" className="hover:text-white transition-colors">Security</a>
          <a href="#" className="hover:text-white transition-colors">Terms</a>
          <a href="#" className="hover:text-white transition-colors">Privacy</a>
        </div>
      </footer>
    </div>
  );
}

// FAQ data — must stay in sync with the FAQPage JSON-LD block in
// /app/frontend/public/index.html. Keep answers truthful — Google penalizes
// FAQ schema that doesn't match visible content.
const FAQS = [
  {
    q: "Who is RealtyVault Manager built for?",
    a: "Commercial real-estate investors running 2-200 properties who want to stop wrestling spreadsheets. We're purpose-built for CRE (office, retail, multifamily, industrial, mixed-use) — not residential landlords.",
  },
  {
    q: "Can my tenants pay rent online?",
    a: "Yes. Tenants get their own portal, pay rent via card or ACH through Stripe Connect, and see their full payment history. You receive the funds directly into your connected Stripe account — we never hold your money.",
  },
  {
    q: "Does it generate a Schedule of Real Estate Owned (SREO) for my lender?",
    a: "Yes. One click produces your branded SREO with per-property cost basis, current market value, NOI, cap rate, loan balance, equity, DSCR, and LTV — plus optional nested rent-roll per property. Download the PDF or email it directly to a saved lender contact list.",
  },
  {
    q: "How does cap-rate and NOI tracking work?",
    a: "Live. Every rent payment, vendor invoice, loan payment, and stored annual expense (taxes + insurance) updates your NOI, cap rate, DSCR, and LTV in real time. No monthly close; no end-of-quarter spreadsheet refresh.",
  },
  {
    q: "Is there a free trial?",
    a: "Yes — start a free workspace with no credit card required. You can add properties, invite tenants, and generate reports immediately. We don't charge until you're ready to scale.",
  },
  {
    q: "What makes this different from Buildium or AppFolio?",
    a: "We're CRE-first, investor-first, and report-first. Cap-rate benchmarking, IRR and DSCR math, branded covenant-grade SREO + rent roll, automated investor digests, and on-demand valuation are first-class features — not add-ons. The tenant portal is a side effect, not the centerpiece.",
  },
];

function FaqItem({ q, a, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="py-5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start justify-between gap-6 text-left group"
        aria-expanded={open}
      >
        <span className="text-lg font-semibold text-white group-hover:text-amber-200 transition-colors" style={{ fontFamily: "Outfit" }}>
          {q}
        </span>
        <ChevronDown
          className={`w-5 h-5 mt-1 text-white/40 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <p className="mt-3 text-white/70 leading-relaxed max-w-3xl">
          {a}
        </p>
      )}
    </div>
  );
}
